import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Tag } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Separator } from '../components/ui/separator';
import { useCart } from '../context/CartContext';
import ProductCarousel from '../components/products/ProductCarousel';
import ProductGridSkeleton from '../components/products/ProductGridSkeleton';
import MobileStickyBar from '../components/layout/MobileStickyBar';
import { getBestSellers } from '../services/products';
import { formatCurrency } from '../lib/currency';

const CartPage = () => {
  const { t } = useTranslation();
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    cartTotal,
    cartCount,
  } = useCart();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = React.useState('');
  const [promoApplied, setPromoApplied] = React.useState(false);
  const [suggestedProducts, setSuggestedProducts] = React.useState([]);
  const [loadingSuggested, setLoadingSuggested] = React.useState(false);

  // Prices are stored in TRY
  const TRY_RATE = 1;
  const cartTotalTRY = cartTotal * TRY_RATE;
  const shipping = cartTotalTRY >= 500 ? 0 : 239.7; // ~8 USD in TRY
  const discount = promoApplied ? cartTotalTRY * 0.1 : 0;
  const tax = (cartTotalTRY - discount) * 0.18; // Turkish VAT is 18%
  const orderTotal = cartTotalTRY - discount + shipping + tax;

  React.useEffect(() => {
    const fetchSuggested = async () => {
      try {
        setLoadingSuggested(true);
        const products = await getBestSellers();
        const filtered = products
          .filter((p) => !cartItems.find((item) => item.id === p.id))
          .slice(0, 8);
        setSuggestedProducts(filtered);
      } catch (error) {
        console.error('Error fetching suggested products:', error);
      } finally {
        setLoadingSuggested(false);
      }
    };

    if (cartItems.length > 0) {
      fetchSuggested();
    }
  }, [cartItems]);

  const handleApplyPromo = () => {
    if (promoCode.toLowerCase() === 'extra20' || promoCode.toLowerCase() === 'save10') {
      setPromoApplied(true);
    }
  };

  if (cartItems.length === 0) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <ShoppingBag size={64} className="mx-auto text-gray-300 mb-6" />
            <h1 className="text-2xl font-semibold mb-4">{t('cart.empty')}</h1>
            <p className="text-gray-600 mb-8">
              {t('cart.emptyDescription')}
            </p>
            <Link to="/">
              <Button size="lg" className="bg-black hover:bg-gray-800">
                {t('common.shopNow')}
              </Button>
            </Link>
          </div>

          {/* Suggested Products */}
          {loadingSuggested ? (
            <div className="mt-16">
              <h2 className="text-2xl font-bold mb-6 text-center">Beğenebileceğiniz Ürünler</h2>
              <ProductGridSkeleton columns={4} count={4} />
            </div>
          ) : suggestedProducts.length > 0 && (
            <div className="mt-16">
              <ProductCarousel
                title={t('product.youMightLike')}
                products={suggestedProducts}
              />
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 lg:pb-8">
        <h1 className="text-3xl font-semibold mb-8">
          {t('cart.title')} ({cartCount} {cartCount === 1 ? t('cart.item') : t('cart.items')})
        </h1>

        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <div
                key={item.cartId}
                className="flex gap-4 p-4 bg-white border rounded-lg"
              >
                {/* Product Image */}
                <Link to={`/product/${item.id}`} className="flex-shrink-0">
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-24 h-32 sm:w-32 sm:h-40 object-cover rounded-md"
                  />
                </Link>

                {/* Product Details */}
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between">
                    <div>
                      <Link
                        to={`/product/${item.id}`}
                        className="font-medium hover:text-blue-600 transition-colors line-clamp-2"
                      >
                        {item.name}
                      </Link>
                      <p className="text-sm text-gray-500 mt-1">
                        {t('product.size')}: {item.size} | {t('product.color')}: {item.color}
                      </p>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.cartId)}
                      className="text-gray-400 hover:text-red-500 transition-colors p-1"
                      aria-label={t('common.remove')}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="mt-auto flex items-center justify-between pt-4">
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateQuantity(item.cartId, item.quantity - 1)
                        }
                        disabled={item.quantity <= 1}
                        className="w-8 h-8 border rounded flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() =>
                          updateQuantity(item.cartId, item.quantity + 1)
                        }
                        className="w-8 h-8 border rounded flex items-center justify-center hover:bg-gray-50 transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      <p className="font-semibold">
                        {formatCurrency(item.price * item.quantity * TRY_RATE)}
                      </p>
                      {item.quantity > 1 && (
                        <p className="text-sm text-gray-500">
                          {formatCurrency(item.price * TRY_RATE)} {t('cart.each')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="mt-8 lg:mt-0">
            <div className="bg-gray-50 rounded-lg p-6 sticky top-28">
              <h2 className="text-lg font-semibold mb-4">{t('cart.orderSummary')}</h2>

              {/* Promo Code */}
              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">{t('cart.promoCode')}</label>
                <div className="flex gap-2">
                  <Input
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder={t('cart.enterCode')}
                    disabled={promoApplied}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    onClick={handleApplyPromo}
                    disabled={promoApplied || !promoCode}
                  >
                    {t('common.apply')}
                  </Button>
                </div>
                {promoApplied && (
                  <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                    <Tag size={14} /> {t('cart.promoApplied')}
                  </p>
                )}
              </div>

              <Separator className="my-4" />

              {/* Totals */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('cart.subtotal')}</span>
                  <span>{formatCurrency(cartTotalTRY)}</span>
                </div>
                {promoApplied && (
                  <div className="flex justify-between text-green-600">
                    <span>{t('cart.discount')}</span>
                    <span>-{formatCurrency(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('cart.shipping')}</span>
                  <span>
                    {shipping === 0 ? (
                      <span className="text-green-600">{t('cart.freeShipping')}</span>
                    ) : (
                      formatCurrency(shipping)
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('cart.tax')}</span>
                  <span>{formatCurrency(tax)}</span>
                </div>
              </div>

              <Separator className="my-4" />

              <div className="flex justify-between font-semibold text-lg">
                <span>{t('cart.total')}</span>
                <span>{formatCurrency(orderTotal)}</span>
              </div>

              {cartTotalTRY < 500 && (
                <p className="text-sm text-gray-500 mt-2">
                  {t('cart.freeShippingThreshold', { amount: formatCurrency(500 - cartTotalTRY, { showSymbol: false }) })}
                </p>
              )}

              <Button
                className="w-full mt-6 bg-black hover:bg-gray-800"
                size="lg"
                onClick={() => navigate('/checkout')}
              >
                {t('cart.proceedToCheckout')}
                <ArrowRight size={18} className="ml-2" />
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                {t('cart.secureCheckout')}
              </p>
            </div>
          </div>
        </div>

        {/* Suggested Products */}
        {loadingSuggested ? (
          <div className="mt-16">
            <h2 className="text-2xl font-bold mb-6 text-center">Beğenebileceğiniz Ürünler</h2>
            <ProductGridSkeleton columns={4} count={4} />
          </div>
        ) : suggestedProducts.length > 0 && (
          <div className="mt-16">
            <ProductCarousel
              title={t('product.frequentlyBoughtTogether')}
              products={suggestedProducts.slice(0, 4)}
            />
          </div>
        )}
      </div>
      <MobileStickyBar
        price={formatCurrency(orderTotal)}
        priceLabel={t('cart.total')}
        ctaLabel={t('cart.proceedToCheckout')}
        onCtaClick={() => navigate('/checkout')}
      />
    </Layout>
  );
};

export default CartPage;
